import React, { PropsWithChildren, useContext, useMemo } from 'react';
import { ListBox, ListBoxOptionProps, ListBoxProps } from '../ListBox';
import { applyProps, extractProps } from '../helpers';

import OptionGroup, {
  OptionGroupProps,
} from '../ListBox/components/OptionGroup';
import { SelectContext } from './context/SelectProvider';
import SelectListBoxOption from './SelectListBoxOption';
import { SelectOption } from '../types';
import SelectAllOption from '../ListBox/components/SelectAllOption';

export type SelectListBoxProps<T> = PropsWithChildren<
  Pick<ListBoxProps<T>, 'searchable' | 'highlightedText'> & {
    minGroupsToDisplayHeader?: number;
  }
>;

const SelectListBox = <T extends SelectOption>({
  children,
  minGroupsToDisplayHeader = 2,
  ...props
}: SelectListBoxProps<T>) => {
  const {
    multiple,
    selectAllIsSelected,
    searchInputValue,
    filteredOptions,
    selectedOptionsWithValuesOnly,
    optionsRef,
    showSelectAll,
  } = useContext(SelectContext);

  const filteredOptionChildren = applyProps(children, (child) => {
    const isOption =
      child.type === ListBox.Option ||
      (child as JSX.Element).type.displayName === ListBox.Option.displayName;
    const isOptionGroup = child.type === OptionGroup;
    const isOptionSelectAll = child.type === SelectAllOption;

    if (isOptionSelectAll && !showSelectAll) {
      return;
    }

    if (isOption) {
      const childProps = child.props as ListBoxOptionProps<T> & {
        value?: SelectOption['value'];
      };
      const value = childProps.option?.value ?? childProps?.value;
      const shouldBeInResultSet = filteredOptions.find(
        (o) => o.value === value,
      );
      if (!shouldBeInResultSet) {
        return;
      }
    }

    if (isOptionGroup) {
      const childProps = child.props as OptionGroupProps;
      const groupChildren = React.Children.toArray(childProps.children);
      if (groupChildren.length !== 0) {
        return React.cloneElement(child);
      }
      return null;
    }
    return React.cloneElement(child);
  });

  const optionGroupsProps = useMemo(
    () =>
      extractProps(
        filteredOptionChildren,
        (child) => child.type === OptionGroup,
      ),
    [filteredOptionChildren],
  );

  const filteredChildren = applyProps(filteredOptionChildren, (child) => {
    const isOptionGroup = child.type === OptionGroup;

    if (isOptionGroup && optionGroupsProps.length < minGroupsToDisplayHeader) {
      const childProps = child.props as OptionGroupProps;
      return childProps.children;
    }

    if (!isOptionGroup) {
      const { value } = child?.props as SelectOption;
      const newIndex =
        filteredOptions.findIndex((o) => o.value === value) +
        (showSelectAll ? 1 : 0);

      return React.cloneElement(child as React.ReactElement, {
        optionRef: optionsRef[newIndex],
      });
    }

    return React.cloneElement(child);
  });

  return (
    <ListBox
      {...(props as ListBoxProps<T>)}
      searchInputValue={searchInputValue}
      selectAllIsSelected={selectAllIsSelected}
      selectedOptionsWithValuesOnly={selectedOptionsWithValuesOnly ?? []}
      multiple={multiple}
    >
      {filteredChildren}
    </ListBox>
  );
};

SelectListBox.displayName = 'ListBox';

SelectListBox.Option = SelectListBoxOption;
SelectListBox.OptionGroup = ListBox.OptionGroup;
SelectListBox.SelectAllOption = ListBox.SelectAllOption;

export default SelectListBox;
